// @flow
import * as SearchAPI from 'lib/api/search';
import { type ActionType, createAction, handleActions } from 'redux-actions';
import { applyPenders } from 'redux-pender';
import { type GenericResponseAction } from 'lib/common';
import produce from 'immer';
import storage from 'lib/storage';
import { type PostItem } from './listing';

const PUBLIC_SEARCH = 'search/PUBLIC_SEARCH';
const PUBLIC_AUTO_COMPLETE_SEARCH = 'search/PUBLIC_AUTO_COMPLETE_SEARCH';
const NEXT_PUBLIC_SEARCH = 'search/NEXT_PUBLIC_SEARCH';
const INITIALIZE = 'search/INITIALIZE';
const INITIALIZE_SEARCH_HISTORY = 'search/INITIALIZE_SEARCH_HISTORY';
const INSERT_SEARCH_HISTORY = 'search/INSERT_SEARCH_HISTORY';
const DELETE_SEARCH_HISTORY = 'search/DELETE_SEARCH_HISTORY';
const REMOVE_SEARCH_HISTORY = 'search/REMOVE_SEARCH_HISTORY';
const SET_SCROLL_POSITION = 'search/SET_SCROLL_POSITION';
const SET_VISIBLE_ITEM_INDEX = 'search/SET_VISIBLE_ITEM_INDEX';
const CLEAR_SEARCH_RESULTS = 'search/CLEAR_SEARCH_RESULTS';

const initializeSearchHistory = createAction(INITIALIZE_SEARCH_HISTORY, (list: string) => list);
const insertSearchHistory = createAction(INSERT_SEARCH_HISTORY, (keyword: string) => keyword);
const deleteSearchHistory = createAction(DELETE_SEARCH_HISTORY, (keyword: string) => keyword);
const removeSearchHistory = createAction(REMOVE_SEARCH_HISTORY);
const setScrollPosition = createAction(SET_SCROLL_POSITION, (position: number) => position);
const setLastViewedItemId = createAction(SET_VISIBLE_ITEM_INDEX, (itemId: string) => itemId);

type InitializeAction = ActionType<typeof initializeSearchHistory>;
type InsertAction = ActionType<typeof insertSearchHistory>;
type deleteAction = ActionType<typeof deleteSearchHistory>;
type removeAction = ActionType<typeof removeSearchHistory>;
type SetScrollPositionAction = ActionType<typeof setScrollPosition>;

export const actionCreators = {
  publicSearch: createAction(
    PUBLIC_SEARCH,
    SearchAPI.search,
    (meta: SearchAPI.SearchParams) => meta,
  ),
  publicAutoCompleteSearch: createAction(
    PUBLIC_AUTO_COMPLETE_SEARCH,
    SearchAPI.autoComplete,
    (meta: SearchAPI.SearchParams) => meta,
  ),
  nextPublicSearch: createAction(
    NEXT_PUBLIC_SEARCH,
    SearchAPI.search,
    (meta: SearchAPI.SearchParams) => meta,
  ),
  initialize: createAction(INITIALIZE),
  initializeSearchHistory,
  insertSearchHistory,
  deleteSearchHistory,
  removeSearchHistory,
  setScrollPosition,
};

type PublicSearchPending = {
  type: string,
  meta: SearchAPI.SearchParams,
};

type PublicSearchResponse = GenericResponseAction<
  {
    count: number,
    data: PostItem[],
  },
  void,
>;

export type SearchState = {
  result: ?{
    count: number,
    data: PostItem[],
  },
  autoComplete: ?{
    count: number,
    data: PostItem[],
  },
  currentPage: number,
  currentKeyword: string,
  currentMenu: string,
  currentFacets: string,
  currentSort: string,
  searchHistory: any,
  scrollPosition: number,
  lastViewedItemId: ?string,
};

const initialState = {
  result: null,
  autoComplete: null,
  currentPage: 1,
  currentKeyword: '',
  currentMenu: '',
  currentFacets: '{}',
  currentSort: 'views:desc',
  searchHistory: [],
  scrollPosition: 0,
};

const reducer = handleActions(
  {
    [INITIALIZE]: (state, action) => {
      return produce(state, draft => {
        const searchHistory = storage.get('searchHistory');
        // 검색 결과와 스크롤 위치는 유지하고 검색 히스토리만 초기화
        draft.searchHistory = searchHistory || [];
      });
    },
    [INITIALIZE_SEARCH_HISTORY]: (state, action: InitializeAction) => {
      return produce(state, draft => {
        if (!action || !draft) return;
        const { payload } = action;
        draft.searchHistory = payload;
      });
    },
    [INSERT_SEARCH_HISTORY]: (state, action: InsertAction) => {
      return produce(state, draft => {
        if (!action || !draft) return;
        const { payload } = action;
        if (state.searchHistory.length > 9) {
          draft.searchHistory.length = 9;
        }
        if (state.searchHistory.indexOf(payload) === -1) {
          draft.searchHistory.unshift(payload);
        }
      });
    },
    [DELETE_SEARCH_HISTORY]: (state, action: deleteAction) => {
      return produce(state, draft => {
        if (!action || !draft) return;
        const { payload } = action;
        draft.searchHistory.splice(draft.searchHistory.indexOf(payload), 1);
      });
    },
    [REMOVE_SEARCH_HISTORY]: (state, action: removeAction) => {
      return produce(state, draft => {
        if (!action || !draft) return;
        draft.searchHistory = [];
      });
    },
    [SET_SCROLL_POSITION]: (state, action: SetScrollPositionAction) => {
      return produce(state, draft => {
        if (!action || !draft) return;
        const { payload } = action;
        draft.scrollPosition = payload;
      });
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: PUBLIC_SEARCH,
    onPending: (state: SearchState, action: PublicSearchPending) => {
      const { meta } = action;
      return produce(state, draft => {
        // 새로운 검색어인 경우 이전 결과 초기화
        if (draft.currentKeyword !== meta.q || draft.currentMenu !== meta.menu) {
          draft.result = null;
        }
        draft.currentPage = 1;
        draft.currentKeyword = meta.q;
        draft.currentMenu = meta.menu;
        draft.currentFacets = meta.facets || '{}';
        draft.currentSort = meta.sort || 'views:desc';
      });
    },
    onSuccess: (state: SearchState, action: PublicSearchResponse) => {
      return {
        ...state,
        result: action.payload.data,
      };
    },
  },
  {
    type: PUBLIC_AUTO_COMPLETE_SEARCH,
    onPending: (state: SearchState, action: PublicSearchPending) => {
      const { meta } = action;
      return produce(state, draft => {
        draft.currentPage = 1;
      });
    },
    onSuccess: (state: SearchState, action: PublicSearchResponse) => {
      return {
        ...state,
        autoComplete: action.payload.data,
      };
    },
  },
  {
    type: NEXT_PUBLIC_SEARCH,
    onPending: (state: SearchState, action: PublicSearchPending) => {
      const { meta } = action;
      return produce(state, draft => {
        if (!meta.page) return;
        draft.currentPage = meta.page;
      });
    },
    onSuccess: (state: SearchState, action: PublicSearchResponse) => {
      const { data: result } = action.payload;
      return produce(state, draft => {
        if (!draft.result) return;
        // $FlowFixMe
        draft.result.hits = draft.result.hits.concat(result.hits);
        // $FlowFixMe
        draft.result.estimatedTotalHits = result.estimatedTotalHits;
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/search.js